import type { FilterFn, FilterFnOption, Updater } from "@tanstack/vue-table";
import type { Ref } from "vue";
import { rankings, rankItem } from "@tanstack/match-sorter-utils";
import { booleanFilter, isBooleanFilter } from "../filters/BooleanFacetedFilter";
import { dateFilter, isDateFilter } from "../filters/DateFacetedFilter";
import { isListFilter, listFilter } from "../filters/ListFacetedFilter";
import { isNumberFilter, numberFilter } from "../filters/NumberFacetedFilter";
import { isStringFilter, stringFilter } from "../filters/StringFacetedFilter";

export function valueUpdater<T extends Updater<any>>(updaterOrValue: T, ref: Ref) {
  ref.value = typeof updaterOrValue === "function" ? updaterOrValue(ref.value) : updaterOrValue;
}

export const defaultFilterFn: FilterFn<any> = (row, id, comparator, addMeta) => {
  if (isStringFilter(comparator)) {
    return stringFilter(row, id, comparator, addMeta);
  }

  if (isNumberFilter(comparator)) {
    return numberFilter(row, id, comparator, addMeta);
  }

  if (isListFilter(comparator)) {
    return listFilter(row, id, comparator, addMeta);
  }

  if (isDateFilter(comparator)) {
    return dateFilter(row, id, comparator, addMeta);
  }

  if (isBooleanFilter(comparator)) {
    return booleanFilter(row, id, comparator, addMeta);
  }

  return true;
};

defaultFilterFn.autoRemove = (value) => {
  if (isStringFilter(value)) return !value?.value;
  if (isNumberFilter(value)) return value?.value === null || value?.value === undefined;

  if (isListFilter(value) || isDateFilter(value)) {
    if (Array.isArray(value.value)) return !value.value.length;
    return !value.value;
  }

  if (isBooleanFilter(value)) {
    return value?.value === null || value?.value === undefined;
  }

  return !value;
};

export const fuzzyFilter: FilterFnOption<any> = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value, {
    threshold: rankings.ACRONYM, // Permite busca por acrônimos (ex: "sva" → "Sistema de Vendas Automatizado")
  });

  // Store the ranking info
  addMeta(itemRank);

  // Return if the item should be filtered in/out
  return itemRank.passed;
};
